import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@verantos-dev/app-state-base';
import { selectFullCohortDsl } from '@verantos-dev/app-state-cohort-builder';
import {
  useGenerateSQLTranslationMutation,
  useGetCohortQuery,
  usePostCohortExecutionMutation,
  usePostCohortVersionMutation,
  usePutCohortVersionMutation,
} from '@verantos-dev/data-access-cohorts';
import {
  CohortDefinitionStatus,
  CohortStatusLegacy,
  SelectStatementType,
} from '@verantos-dev/types-cohorts';

type PostCohortExecution = ReturnType<typeof usePostCohortExecutionMutation>[0];

export const useCohortBuilderSaveQuery = (postCohortExecution?: PostCohortExecution) => {
  const { projectId, cohortId, cohortVersionId } = useParams();
  const fullCohortDSL = useAppSelector(selectFullCohortDsl);
  const [isTranslated, setIsTranslated] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    data: cohortData,
    error: cohortDataError,
    isLoading,
    isUninitialized,
  } = useGetCohortQuery(
    {
      projectId: projectId || '',
      cohortId: cohortId || '',
    },
    {
      skip: !projectId || !cohortId,
    }
  );
  const isCohortDataLoading = isLoading || isUninitialized;

  const [generateSQLTranslation, { data: sqlTranslationData }] =
    useGenerateSQLTranslationMutation();

  useEffect(() => {
    if (projectId && cohortId && fullCohortDSL) {
      (async () => {
        try {
          await generateSQLTranslation({
            cohortDsl: fullCohortDSL,
            cohortId,
            projectId,
            selectStmtType: SelectStatementType.ALL,
          });
          setIsTranslated(true);
        } catch (e) {
          console.error('Error generating SQL translation', e);
        }
      })();
    }
  }, [cohortId, fullCohortDSL, generateSQLTranslation, projectId]);

  const [
    postCohortVersion,
    {
      isLoading: isPostCohortVersionLoading,
      error: postCohortVersionError,
      data: postCohortVersionData,
      isSuccess: postCohortVersionSuccess,
    },
  ] = usePostCohortVersionMutation();
  const [
    putCohortVersion,
    {
      isLoading: isPutCohortVersionLoading,
      error: putCohortVersionError,
      data: putCohortVersionData,
      isSuccess: putCohortVersionSuccess,
    },
  ] = usePutCohortVersionMutation();

  /**
   * This function is used to submit the cohort version
   * based on the latest status of the cohort
   *
   * It will create a new version if the status is Processed or Error
   * It will update the existing version if the status is Draft
   * Otherwise, it will do nothing
   * @returns void
   */
  const submitVersion = async () => {
    if (!cohortData?.data || !projectId || !cohortId || !fullCohortDSL) return;

    switch (cohortData?.data.latestStatus) {
      case CohortDefinitionStatus.FINAL:
      case CohortDefinitionStatus.PROCESSING: {
        return;
      }
      case CohortDefinitionStatus.ERROR:
      case CohortDefinitionStatus.PROCESSED: {
        try {
          setIsSubmitted(true);
          if (postCohortExecution) {
            await postCohortExecution({
              projectId,
              profileId: cohortId,
              datasetReleaseId: cohortData.data.latestDatasetReleaseId,
              dslVersion: 'v0',
              dslValue: fullCohortDSL,
              sqlValue: sqlTranslationData?.data?.sqlQuery || '',
            });
          } else {
            const result = await postCohortVersion({
              projectId,
              profileId: cohortId,
              data: {
                dslVersion: cohortData.data.latestCohortVersionId,
                dslValue: fullCohortDSL,
                sqlValue: sqlTranslationData?.data?.sqlQuery || '',
              },
            });
            if (!result.data?.data.cohortVersionId) {
              console.error('Error creating cohort version', result.error);
              return;
            }
          }
        } catch (e) {
          console.error('Error creating cohort version', e);
        }
        break;
      }
      case CohortDefinitionStatus.DRAFT: {
        try {
          setIsSubmitted(true);
          if (postCohortExecution) {
            await postCohortExecution({
              projectId,
              profileId: cohortId,
              datasetReleaseId: cohortData.data.latestDatasetReleaseId,
              versionId: cohortData.data?.latestCohortVersionId,
              dslVersion: cohortData.data?.latestCohortVersionId,
              dslValue: fullCohortDSL,
              sqlValue: sqlTranslationData?.data?.sqlQuery || '',
            });
          } else {
            const result = await putCohortVersion({
              projectId,
              profileId: cohortId,
              versionId: cohortData.data?.latestCohortVersionId,
              data: {
                dslValue: fullCohortDSL,
                sqlValue: sqlTranslationData?.data?.sqlQuery || '',
              },
            });
            if (!result.data?.data.cohortVersionId) {
              console.error('Error creating cohort version', result.error);
              return;
            }
          }
        } catch (e) {
          console.error('Error updating cohort version', e);
        }
        break;
      }
      default:
        return;
    }
  };
  return {
    projectId,
    cohortId,
    cohortVersionId,
    cohortData,
    initialLoadingError: cohortDataError,
    isInitialLoading: isCohortDataLoading || !isTranslated,
    submitVersion,
    isSubmitting: isPostCohortVersionLoading || isPutCohortVersionLoading,
    submittingError: postCohortVersionError || putCohortVersionError,
    submitResponseData: postCohortVersionData || putCohortVersionData,
    isSubmitted,
    isTranslated,
    isSubmitSuccess: postCohortVersionSuccess || putCohortVersionSuccess,
  };
};
